import { ApiService } from '../apiService'
import type {
  UserProfileDto,
  UserAccountDto,
  PendingEmailChangeDto,
  UpdateUserProfileRequest,
  PasswordResetRequest,
  NotificationPreferencesDto,
  UpdateNotificationPreferencesRequest,
  Theme,
  Language,
  MfaStatusDto,
  MfaSetupDto,
  MfaEnableRequest,
  MfaVerifyRequest,
  MfaBackupCodesDto,
  UserSessionDto,
  DeleteSessionsDto,
  DeleteSessionsResponseDto,
  HomeLocationDto,
  UpdateHomeLocationRequest,
  UserSettingsDto,
  SettingsApiResponse,
  UserNotificationSettingsDto,
  JournalSoundSettingsDto,
  UserPrivacySettingsDto,
  UpdateChatAgeRestrictionRequest,
  UpdateHideMessageContentRequest,
  UpdateDefaultChatNotificationModeRequest,
  UpdateHideRecordedWalkFromGhostsRequest,
  ManagedChildAccountDto,
  ParentAccountDto,
  CreateParentalLinkRequest,
  CreateParentalLinkResponse,
  PendingParentalLinkDto,
  PendingOutboundLinkDto,
  ConfirmParentalLinkRequest,
  SetChildPrivacySettingsRequest,
  AdultContentAccessDto,
  AdultContentAccess,
} from '../../types/settings'

export const settingsApi = {
  // --- Phone number verification -------------------------------------------------
  // Attaching a verified number to an account that already exists. Until these, a number could
  // only be verified during registration, so every older account was stuck showing "Not Verified"
  // with no way to change it — and shut out of signing in by text and resetting by SMS.
  //
  // Both steps take the current password: the number can reset the password, so the server will
  // not let a bearer token alone choose it. Blank for a Google/Apple account signed in recently.

  startPhoneVerification: async (phoneNumber: string, currentPassword: string) =>
    ApiService.post<{ success: boolean; message: string }>(
      '/api/me/phone/start-verification',
      { phoneNumber, currentPassword }
    ),

  verifyPhone: async (phoneNumber: string, code: string, currentPassword: string) =>
    ApiService.post<{ success: boolean; message: string }>(
      '/api/me/phone/verify',
      { phoneNumber, code, currentPassword }
    ),

  // ====================
  // USER PROFILE
  // ====================

  // NOTE: there is no `user-settings` controller on the API and there never was — every
  // /api/user-settings/* path 404s. The ones below were repointed at the routes that do
  // exist; the ones still marked DEAD have no server route at all.

  // Get user profile — MeController, [HttpGet("profile")]
  // ====================
  // AGE-RESTRICTED CONTENT
  // ====================

  /** What this person lets through, and how far their recorded age allows them to go. */
  getAdultContentAccess: () =>
    ApiService.get<AdultContentAccessDto>('/api/me/settings/adult-content'),

  /**
   * Change it. Rejected with 400 when the age on file does not allow the level asked for — the
   * message says whether that is because they are too young or because no age is recorded.
   */
  setAdultContentAccess: (adultContentAccess: AdultContentAccess) =>
    ApiService.put<AdultContentAccessDto>('/api/me/settings/adult-content', { adultContentAccess }),

  getUserProfile: () => ApiService.get<UserProfileDto>('/api/me/profile'),

  // Update user profile — MeController exposes POST, not PUT
  updateUserProfile: (data: UpdateUserProfileRequest) =>
    ApiService.post<SettingsApiResponse<UserProfileDto>>('/api/me/profile', data),

  /**
   * Send the signed-in user a password reset code.
   *
   * The same endpoint the signed-out flow uses — there is no separate authenticated one, and
   * routing it through auth means the code, expiry and rate limit are the ones already tested.
   * `method` is fixed here so callers do not have to know the shape.
   */
  requestPasswordReset: (data: PasswordResetRequest) =>
    ApiService.post<SettingsApiResponse<void>>('/api/auth/forgot-password', {
      ...data,
      method: 'email',
    }),

  // DEAD: no /api/*/verify-email route exists. Unused; left visible rather than deleted so
  // the gap is findable if the flow is ever built.
  verifyEmail: (token: string) =>
    ApiService.post<SettingsApiResponse<void>>(`/api/user-settings/verify-email/${token}`),

  // (verifyPhone lived here, pointing at /api/user-settings/verify-phone -- a route that has never
  // existed, in the dead /api/user-settings namespace. Replaced by the working pair at the top of
  // this object, which call /api/me/phone/*.)

  // ====================
  // NOTIFICATION PREFERENCES
  // ====================

  // Get notification preferences
  getNotificationPreferences: () =>
    ApiService.get<NotificationPreferencesDto>('/api/me/settings/notifications'),

  // Update notification preferences
  updateNotificationPreferences: (data: UpdateNotificationPreferencesRequest) =>
    ApiService.put<SettingsApiResponse<NotificationPreferencesDto>>(
      '/api/me/settings/notifications',
      data
    ),

  // New notification settings endpoints (from API update)
  // Get user notification settings
  getUserNotificationSettings: () =>
    ApiService.get<UserNotificationSettingsDto>('/api/me/settings/notifications'),

  // Update user notification settings
  updateUserNotificationSettings: (data: UserNotificationSettingsDto) =>
    ApiService.put<UserNotificationSettingsDto>('/api/me/settings/notifications', data),

  // Journal-entry sound preferences (global + per-adventure-type overrides)
  getJournalSoundSettings: () =>
    ApiService.get<JournalSoundSettingsDto>('/api/me/settings/journal-sound'),

  updateJournalSoundSettings: (data: JournalSoundSettingsDto) =>
    ApiService.put<{ message: string }>('/api/me/settings/journal-sound', data),

  // ====================
  // EDITOR PREFERENCES
  // ====================

  /** The adventure editor's menu style: 0 Full (titles + descriptions), 1 Simplified (titles + icons). */
  getEditorPreferences: () => ApiService.get<{ menuStyle: number }>('/api/me/settings/editor'),

  updateEditorPreferences: (data: { menuStyle: number }) =>
    ApiService.put<{ menuStyle: number }>('/api/me/settings/editor', data),

  // ====================
  // USER PREFERENCES
  // ====================

  // Get user preferences (theme, language, etc.)
  // Note: Preferences are now part of the user account
  getUserPreferences: async () => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')
    return {
      theme: (account.theme || 'System') as Theme,
      language: (account.language || 'en') as Language,
      measurementUnit: account.measurementUnit ?? 1,
      showLocationOnProfile: account.showLocationOnProfile ?? false,
    }
  },

  /**
   * Change some account fields and leave the rest as they are. Same read-then-post as the other
   * account settings here, for settings that save one at a time (language, units, theme).
   */
  updateAccountFields: async (
    fields: Partial<Pick<UserAccountDto, 'theme' | 'language' | 'measurementUnit' | 'showLocationOnProfile'>>,
  ) => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')
    return ApiService.post<UserAccountDto>('/api/me/account', { ...account, ...fields })
  },

  /** Only the privacy-zone radius, leaving the saved home location and address untouched. */
  updatePrivacyZoneMiles: async (miles: number) => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')
    return ApiService.post<UserAccountDto>('/api/me/account', {
      ...account,
      locationSettings: {
        ...(account.locationSettings || {}),
        locationSharingMinHideDistanceMiles: miles,
      },
    })
  },

  // ====================
  // EMAIL CHANGE
  // ====================

  // Changing the account email takes the current password, then a code sent to the NEW address.
  // Nothing changes until the code is entered; the old address is told afterwards.
  startEmailChange: (data: { newEmail: string; currentPassword: string }) =>
    ApiService.post<PendingEmailChangeDto>('/api/me/email-change', data),

  confirmEmailChange: (code: string) =>
    ApiService.post<{ email: string }>('/api/me/email-change/confirm', { code }),

  cancelEmailChange: () => ApiService.delete<void>('/api/me/email-change'),

  getPendingEmailChange: async () => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')
    return account.pendingEmailChange ?? null
  },

  // ====================
  // MULTI-FACTOR AUTHENTICATION (MFA)
  // ====================

  // Get MFA status
  getMfaStatus: () => ApiService.get<MfaStatusDto>('/api/Mfa/status'),

  // Setup MFA (get QR code and secret)
  setupMfa: () => ApiService.post<MfaSetupDto>('/api/Mfa/setup'),

  // Enable MFA (verify and activate)
  enableMfa: (data: MfaEnableRequest) =>
    ApiService.post<void>('/api/Mfa/enable', {
      code: data.verificationCode,
      rememberDevice: false,
    }),

  // Disable MFA
  disableMfa: (data: MfaVerifyRequest) =>
    ApiService.post<void>('/api/Mfa/disable', {
      code: data.code,
      rememberDevice: false,
    }),

  // Replace the backup codes. Needs a current authenticator code: backup codes are a way past
  // two-step sign-in, so making new ones takes the second step itself.
  regenerateMfaBackupCodes: (code: string) =>
    ApiService.post<MfaBackupCodesDto>('/api/Mfa/regenerate-backup-codes', { code }),

  // ====================
  // USER SESSIONS
  // ====================

  // Get all active sessions
  getUserSessions: () => ApiService.get<UserSessionDto[]>('/api/me/sessions'),

  // Revoke a specific session
  revokeSession: (data: DeleteSessionsDto) =>
    ApiService.delete<DeleteSessionsResponseDto>('/api/me/sessions', data),

  // Revoke all sessions except current
  revokeAllSessionsExceptCurrent: () =>
    ApiService.delete<DeleteSessionsResponseDto>('/api/me/sessions', { deleteAllExceptCurrent: true }),

  // ====================
  // HOME LOCATION
  // ====================

  // DEAD: no home-location route exists anywhere on the API — grep found none in any
  // controller. Both of these 404, and both are called. Whatever screen uses them has never
  // worked; it needs a server endpoint, not a client fix.
  // /api/user-settings has no controller — this panel never loaded. The route now exists
  // at me/settings/home-location and returns the DTO directly, like the other settings
  // endpoints; there is no SettingsApiResponse envelope on the server.
  getHomeLocation: () => ApiService.get<HomeLocationDto>('/api/me/settings/home-location'),

  updateHomeLocation: (data: UpdateHomeLocationRequest) =>
    ApiService.put<HomeLocationDto>('/api/me/settings/home-location', data),

  // ====================
  // LOCATION SHARING
  // ====================

  // Get location sharing settings from account
  getLocationSharingSettings: async () => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')

    // Try locationSettings first (new API), fallback to legacy fields
    const showHometown = account.locationSettings?.showHometown ?? account.showHometown ?? true
    const showLocationToFriends = account.locationSettings?.locShare_Friends ?? account.showLocationToFriends ?? false
    const showLocationToGroups = account.locationSettings?.locShare_Group ?? false
    const showLocationToAdventureParticipants =
      account.locationSettings?.showLocationToAdventureParticipants ??
      account.showLocationToAdventureParticipants ?? false

    return {
      showHometown,
      showLocationToFriends,
      showLocationToGroups,
      showLocationToAdventureParticipants,
    }
  },

  // Update location sharing settings
  updateLocationSharingSettings: async (data: {
    showHometown: boolean
    showLocationToFriends: boolean
    showLocationToGroups: boolean
    showLocationToAdventureParticipants: boolean
  }) => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')

    // Build the locationSettings object with updated values
    const locationSettings = {
      ...(account.locationSettings || {}),
      showHometown: data.showHometown,
      locShare_Friends: data.showLocationToFriends,
      locShare_Group: data.showLocationToGroups,
      showLocationToAdventureParticipants: data.showLocationToAdventureParticipants,
    }

    const updatedAccount = await ApiService.post<UserAccountDto>('/api/me/account', {
      ...account,
      locationSettings,
    })

    return {
      success: true,
      data: {
        showHometown: updatedAccount.locationSettings?.showHometown ?? true,
        showLocationToFriends: updatedAccount.locationSettings?.locShare_Friends ?? false,
        showLocationToGroups: updatedAccount.locationSettings?.locShare_Group ?? false,
        showLocationToAdventureParticipants: updatedAccount.locationSettings?.showLocationToAdventureParticipants ?? false,
      },
    }
  },

  // ====================
  // BILLING ADDRESS & HOME LOCATION
  // ====================

  // Get billing address and home location
  getBillingAddress: async () => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')

    // Try to get from locationSettings first (new API structure), fallback to legacy fields
    const homeLatitude = account.locationSettings?.homeLatitude ?? account.homeLatitude ?? null
    const homeLongitude = account.locationSettings?.homeLongitude ?? account.homeLongitude ?? null
    const locationSharingMinHideDistanceMiles =
      account.locationSettings?.locationSharingMinHideDistanceMiles ??
      account.locationSharingMinHideDistanceMiles ??
      null

    return {
      billingAddress: account.billingAddress || {
        addressLine1: '',
        addressLine2: '',
        city: '',
        stateOrProvince: '',
        postalCode: '',
        country: '',
      },
      homeLatitude,
      homeLongitude,
      locationSharingMinHideDistanceMiles,
    }
  },

  // Update billing address and home location
  updateBillingAddress: async (data: {
    billingAddress: {
      addressLine1: string
      addressLine2: string
      city: string
      stateOrProvince: string
      postalCode: string
      country: string
    }
    homeLatitude: number | null
    homeLongitude: number | null
    locationSharingMinHideDistanceMiles: number | null
  }) => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')

    // Build the locationSettings object with updated values
    const locationSettings = {
      ...(account.locationSettings || {}),
      homeLatitude: data.homeLatitude,
      homeLongitude: data.homeLongitude,
      locationSharingMinHideDistanceMiles: data.locationSharingMinHideDistanceMiles,
      homeLocationLastUpdated: new Date().toISOString(),
    }

    await ApiService.post<UserAccountDto>('/api/me/account', {
      ...account,
      billingAddress: data.billingAddress,
      locationSettings,
    })
  },

  // ====================
  // COMBINED SETTINGS
  // ====================

  // Get all user settings at once (optional optimization endpoint)
  getAllUserSettings: () => ApiService.get<UserSettingsDto>('/api/user-settings'),

  // ====================
  // PRIVACY SETTINGS
  // ====================

  // Get privacy settings (chat age restrictions)
  getPrivacySettings: () => ApiService.get<UserPrivacySettingsDto>('/api/me/settings/privacy'),

  // Update chat age restrictions
  updateChatAgeRestriction: (data: UpdateChatAgeRestrictionRequest) =>
    ApiService.put<UserPrivacySettingsDto>('/api/me/settings/privacy/chat-age-restriction', data),

  // Hide chat message content on the lock screen (push notifications)
  updateHideMessageContent: (data: UpdateHideMessageContentRequest) =>
    ApiService.put<UserPrivacySettingsDto>('/api/me/settings/privacy/hide-message-content', data),

  // Global default chat notification level (applies to non-overridden conversations)
  updateDefaultChatNotificationMode: (data: UpdateDefaultChatNotificationModeRequest) =>
    ApiService.put<UserPrivacySettingsDto>('/api/me/settings/privacy/default-chat-notification-mode', data),

  // Whether this account's recorded walks may be replayed as ghosts for other players. Turning it
  // on costs nothing on a leaderboard — the time still ranks, and still produces a ghost.
  updateHideRecordedWalkFromGhosts: (data: UpdateHideRecordedWalkFromGhostsRequest) =>
    ApiService.put<UserPrivacySettingsDto>('/api/me/settings/privacy/hide-recorded-walk-from-ghosts', data),

  // ====================
  // PARENTAL CONTROLS
  // ====================

  // Get managed child accounts (for parents)
  getManagedChildren: () => ApiService.get<ManagedChildAccountDto[]>('/api/me/parental/children'),

  // Get parent accounts (for children)
  getParentAccounts: () => ApiService.get<ParentAccountDto[]>('/api/me/parental/parents'),

  // Create a parental link request (parent initiates)
  createParentalLink: (data: CreateParentalLinkRequest) =>
    ApiService.post<CreateParentalLinkResponse>('/api/me/parental/link', data),

  // Get pending link requests (for children to confirm)
  getPendingLinkRequests: () => ApiService.get<PendingParentalLinkDto[]>('/api/me/parental/requests'),

  // Confirm a parental link (child confirms with verification code)
  confirmParentalLink: (data: ConfirmParentalLinkRequest) =>
    ApiService.post<void>('/api/me/parental/confirm', data),

  // Revoke a parental link
  revokeParentalLink: (linkId: string) =>
    ApiService.delete<void>(`/api/me/parental/link/${linkId}`),

  // Update child's privacy settings (parent manages child)
  updateChildPrivacySettings: (data: SetChildPrivacySettingsRequest) =>
    ApiService.put<UserPrivacySettingsDto>('/api/me/parental/child-settings', data),

  // Get pending outbound link requests (for parents to see their sent requests)
  getPendingOutboundRequests: () =>
    ApiService.get<PendingOutboundLinkDto[]>('/api/me/parental/pending'),

  // Cancel/recall a pending outbound request (parent cancels)
  cancelPendingRequest: (linkId: string) =>
    ApiService.delete<void>(`/api/me/parental/pending/${linkId}`),

  // Decline a link request (child declines)
  declineParentalLink: (linkId: string) =>
    ApiService.post<void>(`/api/me/parental/decline/${linkId}`),
}
